import { cn } from '@/ui';
import type { StepDefinition, StepId } from './types';

interface StepperProps {
  steps: StepDefinition[];
  currentStep: StepId;
  /** Steps with data persisted — render with a filled green circle + check. */
  completedSteps: Set<StepId>;
  /** Steps the user advanced past without saving — render outlined + dim. */
  skippedSteps: Set<StepId>;
  onStepClick: (id: StepId) => void;
}

// Vertical stepper. Renders as a sidebar on desktop and a horizontal scroller
// on mobile. Steps are clickable to jump back to a previous step (forward
// jumps are disabled to preserve the linear save flow — clicking forward
// would skip persistence on the in-between steps).
export function Stepper({ steps, currentStep, completedSteps, skippedSteps, onStepClick }: StepperProps) {
  const currentIndex = steps.findIndex((s) => s.id === currentStep);

  return (
    <nav aria-label="Onboarding progress" className="min-w-0 lg:sticky lg:top-6">
      <ol className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible -mx-4 px-4 lg:mx-0 lg:px-0">
        {steps.map((step, index) => {
          const isCurrent = step.id === currentStep;
          const isDone = completedSteps.has(step.id) && !isCurrent;
          const isSkipped = skippedSteps.has(step.id) && !isCurrent && !isDone;
          const isClickable = index <= currentIndex || isDone || isSkipped;

          return (
            <li key={step.id} className="shrink-0 lg:shrink">
              <button
                type="button"
                disabled={!isClickable}
                onClick={() => isClickable && onStepClick(step.id)}
                className={cn(
                  'w-full text-left flex items-start gap-3 rounded-lg px-3 py-2.5 transition-colors',
                  'disabled:cursor-not-allowed',
                  isCurrent && 'bg-brand-soft',
                  !isCurrent && isClickable && 'hover:bg-surface-2',
                )}
              >
                <span
                  className={cn(
                    'shrink-0 mt-0.5 h-6 w-6 rounded-full text-body-xs font-semibold flex items-center justify-center',
                    isDone && 'bg-brand text-brand-ink',
                    isCurrent && 'bg-brand text-brand-ink',
                    isSkipped && 'border border-border-strong text-ink-faint bg-transparent',
                    !isCurrent && !isDone && !isSkipped && 'bg-surface-2 text-ink-subtle',
                  )}
                  aria-hidden
                >
                  {isDone ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : isSkipped ? (
                    // En-dash, not a number: conveys "you went past this but
                    // didn't save anything" without claiming completion.
                    <span aria-hidden>–</span>
                  ) : (
                    index + 1
                  )}
                </span>
                <span className="min-w-0">
                  <span className={cn(
                    'block text-body-sm font-medium',
                    isSkipped ? 'text-ink-subtle' : 'text-ink',
                  )}>
                    {step.label}
                    {isSkipped && (
                      <span className="ml-1.5 text-caption font-normal text-ink-faint">
                        Skipped
                      </span>
                    )}
                  </span>
                  {step.hint && (
                    <span className="block text-caption text-ink-subtle truncate lg:whitespace-normal">
                      {step.hint}
                    </span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
